import { Controller } from "@hotwired/stimulus"

/**
 * Generates an "On this page" table of contents from h2 headings
 * in the help article content. Highlights the currently visible
 * section as the user scrolls.
 */
export default class extends Controller {
  static targets = ["list"]
  static values = { selector: { type: String, default: "h2" } }

  connect() {
    this._buildToc()
    this._observeHeadings()
  }

  disconnect() {
    if (this._observer) this._observer.disconnect()
  }

  _buildToc() {
    const content = document.querySelector(".help-content.markdown-body")
    if (!content) return

    const headings = content.querySelectorAll(this.selectorValue)
    if (headings.length < 2) {
      this.element.hidden = true
      return
    }

    const list = this.listTarget
    list.innerHTML = ""
    this._links = new Map()

    headings.forEach((h) => {
      if (!h.id) return

      const li = document.createElement("li")
      const a = document.createElement("a")
      a.href = `#${h.id}`
      a.textContent = h.textContent
      a.className =
        "block py-1 text-[13px] leading-snug text-white/35 transition-colors duration-150 hover:text-white/70"
      a.addEventListener("click", (e) => {
        e.preventDefault()
        h.scrollIntoView({ behavior: "smooth", block: "start" })
        history.replaceState(null, "", `#${h.id}`)
      })
      li.appendChild(a)
      list.appendChild(li)
      this._links.set(h.id, a)
    })
  }

  _observeHeadings() {
    const content = document.querySelector(".help-content.markdown-body")
    if (!content || !this._links || this._links.size === 0) return

    const headings = content.querySelectorAll(this.selectorValue)

    this._observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            this._setActive(entry.target.id)
          }
        })
      },
      { rootMargin: "-80px 0px -70% 0px", threshold: 0 },
    )

    headings.forEach((h) => {
      if (h.id) this._observer.observe(h)
    })
  }

  _setActive(id) {
    this._links.forEach((a, headingId) => {
      if (headingId === id) {
        a.className =
          "block py-1 text-[13px] leading-snug text-accent-primary font-medium transition-colors duration-150"
      } else {
        a.className =
          "block py-1 text-[13px] leading-snug text-white/35 transition-colors duration-150 hover:text-white/70"
      }
    })
  }
}
